import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FocusSession, Task } from '@/types/task';
import { 
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, 
  startOfDay, endOfDay, subDays, format, isWithinInterval 
} from 'date-fns';

interface TopTask {
  taskContent: string;
  totalMinutes: number;
}

interface FastestTask {
  taskContent: string;
  durationMinutes: number;
}

interface PeriodStats {
  totalMinutes: number;
  topTask: TopTask | null;
  fastestTask: FastestTask | null;
}

export type StatsPeriod = 'today' | 'week' | 'month';
export type SummaryPeriod = 'yesterday' | 'today' | 'week' | 'month';

interface MultiPeriodStats {
  today: PeriodStats;
  week: PeriodStats;
  month: PeriodStats;
}

interface UseMultiPeriodFocusStatsResult {
  stats: MultiPeriodStats;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

function calculateStatsForSessions(
  sessions: FocusSession[],
  completedTaskContents: Set<string>
): PeriodStats {
  const safeSessions = sessions.filter((s) => s != null && s.started_at != null);
  const totalMinutes = safeSessions.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0);

  // Top task by cumulative time
  const taskTimeMap = new Map<string, { content: string; total: number }>();
  safeSessions.forEach((s) => {
    const content = s.task_content ?? '';
    const mins = Number(s.duration_minutes) || 0;
    const existing = taskTimeMap.get(content) || { content, total: 0 };
    existing.total += mins;
    taskTimeMap.set(content, existing);
  });

  let topTask: TopTask | null = null;
  let maxTime = 0;
  taskTimeMap.forEach((value) => {
    if (value.total > maxTime) {
      maxTime = value.total;
      topTask = { taskContent: value.content, totalMinutes: value.total };
    }
  });

  // Fastest completed task
  let fastestTask: FastestTask | null = null;
  safeSessions.forEach((s) => {
    const content = s.task_content ?? '';
    const mins = Number(s.duration_minutes) || 0;
    if (completedTaskContents.has(content) && mins > 0) {
      if (!fastestTask || mins < fastestTask.durationMinutes) {
        fastestTask = {
          taskContent: content,
          durationMinutes: mins,
        };
      }
    }
  });

  return { totalMinutes, topTask, fastestTask };
}

export function useMultiPeriodFocusStats(
  currentMonth: Date,
  completedTasks: Task[]
): UseMultiPeriodFocusStatsResult {
  const [sessions, setSessions] = useState<FocusSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    const timeout = setTimeout(() => setIsLoading(false), 15000);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSessions([]);
        clearTimeout(timeout);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('focus_sessions')
        .select('*')
        .eq('user_id', user.id)
        .gte('started_at', monthStart.toISOString())
        .lte('started_at', monthEnd.toISOString());

      if (error) throw error;
      setSessions((data || []) as FocusSession[]);
    } catch (err) {
      console.error('Error fetching focus sessions:', err);
      setSessions([]);
    } finally {
      clearTimeout(timeout);
      setIsLoading(false);
    }
  }, [monthStart.toISOString(), monthEnd.toISOString()]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const stats = useMemo(() => {
    const completedTaskContents = new Set(completedTasks.map(t => t.content));
    const now = new Date();

    // Define intervals
    const todayInterval = {
      start: startOfDay(now),
      end: endOfDay(now),
    };
    const weekInterval = {
      start: startOfWeek(now, { weekStartsOn: 1 }),
      end: endOfWeek(now, { weekStartsOn: 1 }),
    };

    // Filter sessions for each period (skip invalid or missing started_at)
    const validSessions = sessions.filter(
      (s) => s != null && s.started_at != null && !Number.isNaN(new Date(s.started_at).getTime())
    );
    const todaySessions = validSessions.filter((s) =>
      isWithinInterval(new Date(s.started_at), todayInterval)
    );
    const weekSessions = validSessions.filter((s) =>
      isWithinInterval(new Date(s.started_at), weekInterval)
    );
    const monthSessions = validSessions;

    return {
      today: calculateStatsForSessions(todaySessions, completedTaskContents),
      week: calculateStatsForSessions(weekSessions, completedTaskContents),
      month: calculateStatsForSessions(monthSessions, completedTaskContents),
    };
  }, [sessions, completedTasks]);

  return {
    stats,
    isLoading,
    refetch: fetchSessions,
  };
}

// Hook for fetching data for AI summary with flexible periods
export function useSummaryData() {
  const [isLoading, setIsLoading] = useState(false);

  const fetchSummaryData = useCallback(async (period: SummaryPeriod) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const now = new Date();
      let periodStart: Date;
      let periodEnd: Date;
      let periodLabel: string;

      switch (period) {
        case 'yesterday':
          periodStart = startOfDay(subDays(now, 1));
          periodEnd = endOfDay(subDays(now, 1));
          periodLabel = 'Yesterday';
          break;
        case 'today':
          periodStart = startOfDay(now);
          periodEnd = endOfDay(now);
          periodLabel = 'Today';
          break;
        case 'week':
          periodStart = startOfWeek(now, { weekStartsOn: 1 });
          periodEnd = endOfWeek(now, { weekStartsOn: 1 });
          periodLabel = `${format(periodStart, 'MMM d')} - ${format(periodEnd, 'MMM d')}`;
          break;
        case 'month':
          periodStart = startOfMonth(now);
          periodEnd = endOfMonth(now);
          periodLabel = format(now, 'MMMM yyyy');
          break;
      }

      // Fetch tasks, sessions, and statuses for the period in parallel
      const [tasksResult, sessionsResult, statusResult] = await Promise.all([
        supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user.id)
          .gte('day', format(periodStart, 'yyyy-MM-dd'))
          .lte('day', format(periodEnd, 'yyyy-MM-dd')),
        supabase
          .from('focus_sessions')
          .select('*')
          .eq('user_id', user.id)
          .gte('started_at', periodStart.toISOString())
          .lte('started_at', periodEnd.toISOString()),
        supabase
          .from('daily_status')
          .select('*')
          .eq('user_id', user.id)
          .gte('date', format(periodStart, 'yyyy-MM-dd'))
          .lte('date', format(periodEnd, 'yyyy-MM-dd')),
      ]);

      return {
        tasks: tasksResult.data || [],
        sessions: sessionsResult.data || [],
        statuses: statusResult.data || [],
        period,
        periodStart: format(periodStart, 'MMM d'),
        periodEnd: format(periodEnd, 'MMM d'),
        periodLabel,
      };
    } catch (err) {
      console.error('Error fetching summary data:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { fetchSummaryData, isLoading };
}
